import { ApplicationView } from '@shared/enums';
import { useSantaClausLetterCustomerDescription } from '@shared/hooks/useSantaClausLetterCustomerDescription';
import { Avatar } from '@shared/ui/Avatar';
import { GenericLink } from '@shared/ui/GenericLink';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegUserCircle } from 'react-icons/fa';
import { ApplicationItem, Customer } from './types';

type ApplicationCardWhoNeedsProps = {
  customer: Customer;
  view: ApplicationView;
  items: ApplicationItem[];
  withoutClamp?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

function useApplicationCardWhoNeedsState(props: ApplicationCardWhoNeedsProps) {
  const { customer, items, view } = props;
  /**
   * NOTE: After Santa Claus event we can use `useCustomerDescription` hook instead
   */
  const description = useSantaClausLetterCustomerDescription(customer, items, view);

  return {
    withoutClamp: props.withoutClamp || false,
    customer,
    description,
  } as const;
}

export const ApplicationCardWhoNeeds = (props: ApplicationCardWhoNeedsProps) => {
  const { customer, description, withoutClamp } = useApplicationCardWhoNeedsState(props);
  const { t } = useTranslation();

  return (
    <div className={props.className}>
      <div className="flex items-center space-x-2 mb-3">
        <div className="text-black-50 text-xxs">{t('who-needs')}</div>
      </div>
      <div className="flex mb-3">
        <GenericLink type="page" to={`/customer/profile/${customer.id}`}>
          {customer.avatar ? (
            <Avatar
              alt=""
              url={customer.avatar}
              size={26}
              loading="lazy"
              className="mr-4 shrink-0"
              placeholder={null}
            />
          ) : (
            <FaRegUserCircle size={26} className="text-black mr-4 shrink-0" />
          )}
        </GenericLink>

        <div className={clsx('text-xs lg:max-w-[360px]', !withoutClamp && 'line-clamp-3')}>{description}</div>
      </div>
    </div>
  );
};
